import * as React from 'react';
import * as trace from 'owa-trace';
import getCanTableLoadMore from 'mini-mail-list-actions/lib/utils/tables/getCanTableLoadMore';
import getIsInCheckedMode from '../../utils/getIsInCheckedMode';
import getSelectedTableView from 'mini-mail-list-store/lib/utils/getSelectedTableView';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import isInLandscapeMode from 'dom-utils/lib/utils/isInLandscapeMode';
import loadMoreInTable from 'mini-mail-list-actions/lib/actions/tables/loadMoreInTable';
import NativeAdUserFlight from 'mini-mail-nativead-userchecker/lib/NativeAdUserFlight';
import shouldShowAutomaticRepliesBanner from '../../utils/mailList/shouldShowAutomaticRepliesBanner';
import shouldShowMailListItem from 'mini-mail-list-view/lib/utils/mailList/shouldShowMailListItem';
import shouldShowStickyFocusedRollup from '../../utils/mailList/shouldShowStickyFocusedRollup';
import TableView from 'mini-mail-list-store/lib/store/schema/TableView';
import VirtualizedMailListItem from './VirtualizedMailListItem';
import { ACTION_NEW_MESSAGE } from 'mini-mail-compose-actions/lib/utils/actionNames';
import { ACTION_SELECT_FILTER } from 'mini-mail-filter-actions/lib/utils/actionNames';
import { canShowFocusedOtherCampaign } from 'mini-mail-list-view/lib/utils/mailList/canShowFocusedOtherCampaign';
import { checkForGreenRoomQueryParam } from 'mini-mail-greenroom/lib/utils/checkForGreenRoomQueryParam';
import { getGreenRoomStore } from 'mini-mail-greenroom/lib/store';
import { getGreenRoomVisible } from 'mini-mail-greenroom/lib/utils/getGreenRoomVisibility';
import { getListViewTypeCustomData } from 'mini-mail-list-store/lib/selectors/getListViewTypeCustomData';
import { getStore as getMailStore } from 'owa-mail-store/lib/store/Store';
import { GreenRoomEnum } from 'mini-mail-greenroom/lib/utils/greenRoomEnum';
import { greenRoomHeightMutator } from 'mini-mail-greenroom/lib/mutators/greenRoomHeightMutator';
import { greenRoomVisibilityMutator } from 'mini-mail-greenroom/lib/mutators/greenRoomVisibilityMutator';
import { isInSearchView } from 'mini-mail-list-store/lib/selectors/isInSearchView';
import { isIOS } from 'mini-device-utils/lib/utils/isIOS';
import { isUnifiedSearchView } from 'mini-mail-list-store/lib/selectors/isUnifiedSearchView';
import { observer } from 'mobx-react';
import { registerWorkflowActions } from 'mini-mail-workflow-tracking/lib/utils/actionToSignalMapping';
import { resetGlobalAdState } from 'mini-mail-nativead-view/lib/utils/getGlobalAdState';
import { shouldShowGreenRoom } from 'mini-mail-greenroom/lib/utils/shouldShowGreenRoom';
import { shouldShowMailListHeaderForFolderId } from 'mini-mail-list-view/lib/utils/mailList/shouldShowMailListHeaderForFolderId';
import { WindowDimensions } from 'mini-hooks/lib/useWindowDimensions';

import {
    ACTION_LOAD_MORE_IN_TABLE,
    ACTION_ON_LOAD_MORE_ITEMS_SUCCESS,
    ACTION_ON_LOAD_MORE_CONVERSATIONS_SUCCESS,
    ACTION_SELECT_FOCUSED_PIVOT,
} from 'mini-mail-list-actions/lib/utils/actionNames';

const styles = require('./VirtualizedMailList.scss');
const classNames = require('classnames/bind');

import VirtualizedList, {
    ItemRendererProp,
} from 'mini-virtualized-list/lib/components/VirtualizedList';
import getListItemHeight, {
    MODULE_SWITCHER_HEIGHT,
    RowComponentsHeight,
    RowComponentsID,
    MULTI_SELECT_FOOTER_HEIGHT,
    UNIFIED_SEARCH_HEADER_HEIGHT,
} from '../../utils/mailList/getListItemHeight';
import { getDisplayCustomisationsContext } from './DisplayCustomisationsProvider ';
import {
    COMPOSE_MAIL_CLICKED,
    LOAD_MORE_MAIL_STARTED,
    LOAD_MORE_MAIL_ENDED,
    FOCUSED_OTHER_TAB_CLICKED,
    FILTER_SELECTED,
} from 'mini-mail-workflow-tracking/lib/constants/ListViewWorkflowConstants';

interface VirtualizedMailListProps {
    showNativeAdFormat: NativeAdUserFlight;
    onLoadMoreItems?: () => Promise<void>;
    forwardedRefs?: {
        switchRef: React.RefObject<HTMLDivElement>;
        filterRef: React.RefObject<HTMLDivElement>;
    };
}

@observer
export default class VirtualizedMailList extends React.Component<VirtualizedMailListProps, {}> {
    static contextType = getDisplayCustomisationsContext();
    // Class variable to avoid recomputation.
    private virtualListItemIds = null;
    private lastDataSourceId = null;
    private itemHeightsInPx = null;
    private forwardedRefs;
    private greenRoomTimeoutId;
    private greenRoomRef = React.createRef<HTMLDivElement>();

    constructor(props: VirtualizedMailListProps) {
        super(props);
        // Refresh Ad when List Mounts
        this.resetAdInstanceState();
        this.forwardedRefs = props.forwardedRefs;
    }

    /**
     * This function ensure nativeAd will refresh when VirtualMailList
     * Mount/UnMount or folder is switched.
     */
    private resetAdInstanceState = () => {
        this.props.showNativeAdFormat !== NativeAdUserFlight.NoNativeAd && resetGlobalAdState();
    };

    private shouldShowListHeader = () => {
        const tableView = getSelectedTableView();

        if (isUnifiedSearchView()) {
            return false;
        }

        return !!(
            tableView?.tableQuery &&
            shouldShowMailListHeaderForFolderId(tableView?.tableQuery?.folderId)
        );
    };

    private getVirtualListItemIds = (): string[] => {
        const tableView = getSelectedTableView();
        const isInMultiSelectMode = getIsInCheckedMode();
        // Note: Order is important
        return [
            this.shouldShowListHeader() && RowComponentsID.ListHeader, // 1. Header
            !isInMultiSelectMode &&
                this.props.showNativeAdFormat === NativeAdUserFlight.PrebidMobileBannerAd &&
                RowComponentsID.AdBannerContainer,
            canShowFocusedOtherCampaign() && RowComponentsID.FocusedOtherCampaign, // 1.a focused other campaign
            !isInMultiSelectMode &&
                shouldShowAutomaticRepliesBanner(tableView) &&
                RowComponentsID.AutomaticReplyBanner, // 2. Automatic Reply Banner
            !isInMultiSelectMode &&
                shouldShowStickyFocusedRollup(tableView) &&
                RowComponentsID.FocusedInboxRollup, // 3. Focused Others Rollup
            /*Add conditions as and when given*/ shouldShowGreenRoom(tableView) &&
                RowComponentsID.GreenRoom,
            !isInMultiSelectMode &&
                getMailStore().showAd &&
                this.props.showNativeAdFormat === NativeAdUserFlight.PrebidMobileAllInbox &&
                RowComponentsID.AdContainerExp,
            ...tableView.itemIds.filter(this.doesTableConversationExistForItem), // 5. Mail Items
            this.getLoadMoreRowComponent(tableView), // 6. Spinner or Load Error
        ].filter(Boolean);
    };

    private getLoadMoreRowComponent = (tableView: TableView) =>
        this.hasMoreItemsOnServer() &&
        (tableView.hasErrorLoadingTable ? RowComponentsID.LoadError : RowComponentsID.Spinner);

    private doesTableConversationExistForItem = (itemId: string): boolean => {
        const tableView = getSelectedTableView();
        return shouldShowMailListItem(itemId, tableView);
    };

    private getItemHeight = (index: number): number => this.itemHeightsInPx[index];

    /**
     * Add SelectedTableView Id also while creating React Key, since it might be possible
     * that two folder have same conversationId in their respective store which might
     * leads to UI inconsistency while switching from one folder to another.
     */
    private getItemReactKey = (index: number, itemData: string[]): string =>
        itemData[index] + getSelectedTableView().id;

    /**
     * This function calculates the height of the virtualize container.
     */
    private getContainerHeight = (windowDimensions: WindowDimensions): number => {
        const windowInnerHeight = windowDimensions.height;
        const isUnifiedSearch = isUnifiedSearchView();
        const isInCheckedMode = getIsInCheckedMode();

        /**
         * In MultiSelect mode, only the MultiSelectHeader is shown in UnifiedSearch
         */
        const listHeaderHeight =
            isUnifiedSearch && !isInCheckedMode
                ? UNIFIED_SEARCH_HEADER_HEIGHT
                : RowComponentsHeight[RowComponentsID.ExtendedListHeader];

        let footerHeight = 0;

        if (getIsInCheckedMode()) {
            footerHeight = MULTI_SELECT_FOOTER_HEIGHT;
        } else if (isInSearchView() || isInLandscapeMode() || isUnifiedSearch) {
            // In Landscape mode, ModuleSwitcher is moved to the left but
            // MultiSelectFooter remains at bottom only.
            footerHeight = 0;
        } else {
            footerHeight = MODULE_SWITCHER_HEIGHT;
        }

        const isFooterPresent: boolean = !!footerHeight;

        /* For iphone X and above, we apply safe area padding to avoid overlap of our app footer with the iphone home button.*/
        if (isIOS() && isFooterPresent) {
            const safeAreaInsetBottomHeight =
                parseFloat(getComputedStyle(document.documentElement)?.getPropertyValue('--sab')) ||
                0;
            footerHeight += safeAreaInsetBottomHeight;
        }
        return windowInnerHeight - listHeaderHeight - footerHeight;
    };

    private hasMoreItemsOnServer = (): boolean => {
        const tableView = getSelectedTableView();
        return getCanTableLoadMore(tableView);
    };

    private getIsItemLoaded = (index: number): boolean => {
        const virtualListItemIds = this.virtualListItemIds;
        // Every item is loaded except for our loading spinner row.
        return (
            index < virtualListItemIds.length &&
            virtualListItemIds[index] !== RowComponentsID.Spinner
        );
    };

    private fetchMoreItems = (): Promise<void> => {
        // If VML is being re-used, such as in case of UnifiedSearch, allow callback from props to be executed
        if (this.props.onLoadMoreItems) {
            return this.props.onLoadMoreItems();
        }
        const tableView = getSelectedTableView();
        if (tableView.hasErrorLoadingTable) {
            return Promise.resolve();
        }
        return loadMoreInTable(tableView);
    };

    private renderMailItem = (props: ItemRendererProp<string>): JSX.Element => (
        <VirtualizedMailListItem
            {...props}
            forwardedRefs={this.forwardedRefs}
            forwardedRef={this.greenRoomRef}
        />
    );

    private isDataSourceSwitching(): boolean {
        // true when we are switching a folder or filter, (datasource is changing)
        return this.lastDataSourceId !== getSelectedTableView().id;
    }

    render() {
        this.virtualListItemIds = this.getVirtualListItemIds();
        this.itemHeightsInPx = this.virtualListItemIds.map(virtualListItemId =>
            getListItemHeight(virtualListItemId, this.context, [
                ['GreenRoom', getGreenRoomStore().greenRoomHeight],
            ])
        );
        const isInCheckedMode = getIsInCheckedMode();

        return (
            <VirtualizedList
                isRTL={getUserConfiguration().SessionSettings.IsUserCultureRightToLeft}
                enableIsScrolling={true}
                shouldScrollToTop={this.isDataSourceSwitching()}
                shouldScrollToBottom={this.virtualListItemIds.includes(RowComponentsID.LoadError)}
                itemData={this.virtualListItemIds}
                containerHeight={this.getContainerHeight}
                itemReactKey={this.getItemReactKey}
                itemHeight={this.getItemHeight}
                getIsItemLoaded={this.getIsItemLoaded}
                fetchMoreItems={this.fetchMoreItems}
                itemCount={this.virtualListItemIds.length}
                itemRenderer={this.renderMailItem}
                className={classNames({
                    [styles.iOSSafeArea]: isIOS() && isInSearchView() && !isInCheckedMode,
                    [styles.unifiedSearchView]: isUnifiedSearchView() && !isInCheckedMode,
                })}
            />
        );
    }

    componentDidUpdate(prevProps: VirtualizedMailListProps) {
        // Updating lastDataSourceId which will be needed for this.isDataSourceSwitching()
        this.lastDataSourceId = getSelectedTableView().id;

        if (!prevProps.showNativeAdFormat && this.props.showNativeAdFormat) {
            /**
             * It is added just to have same telemetry number for ads as
             * compared to the list without virtualization. This will handle scenario where
             * user switch from non-ads showing folder to Inbox.
             */
            resetGlobalAdState();
        }
    }

    checkIfGreenRoomHasLoaded() {
        if (this.greenRoomRef?.current) {
            greenRoomHeightMutator(this.getGreenRoomHeightValue());
        } else {
            this.greenRoomTimeoutId = setTimeout(() => {
                this.checkIfGreenRoomHasLoaded();
            }, 200);
        }
    }

    getGreenRoomHeightValue() {
        return this.greenRoomRef.current?.offsetHeight ?? 125;
    }

    componentWillUnmount() {
        clearTimeout(this.greenRoomTimeoutId);
        window.removeEventListener('resize', () => {
            greenRoomHeightMutator(this.getGreenRoomHeightValue());
        });
    }

    async greenRoomBanner() {
        const getGreenRoomVisibility = await getGreenRoomVisible();
        getGreenRoomVisibility === GreenRoomEnum.Hidden && !checkForGreenRoomQueryParam()
            ? greenRoomVisibilityMutator(GreenRoomEnum.Hidden)
            : greenRoomVisibilityMutator(GreenRoomEnum.Visible);
        this.checkIfGreenRoomHasLoaded();
        window.addEventListener('resize', () => {
            greenRoomHeightMutator(this.getGreenRoomHeightValue());
        });
    }

    async componentDidMount() {
        // Updating lastDataSourceId which will be needed for this.isDataSourceSwitching()
        const selectedTableView = getSelectedTableView();

        this.lastDataSourceId = selectedTableView.id;

        await this.greenRoomBanner();

        // [Live Site Debugger]: WI #80176. Few Consumer users are not able to see their emails.
        requestAnimationFrame(() => {
            try {
                const mainDomNode = document.getElementsByTagName('main')[0];
                const rowsInStoreCount = selectedTableView.itemIds.length;
                if (rowsInStoreCount > 0 && mainDomNode) {
                    const articlesCount = mainDomNode.getElementsByTagName('article')?.length;

                    // Check if there is a mismatch between rows in store and on UI
                    if (articlesCount === 0) {
                        trace.errorThatWillCauseAlert(
                            `[MailListRenderMismatch] UI: ${articlesCount}. Store: ${rowsInStoreCount}. LVT: ${
                                getListViewTypeCustomData().LVT
                            }`
                        );
                    }
                }
            } catch (error) {}
        });

        // register all possible action-to-signal mappings that we're interested in,
        // which may get triggered in future by user actions.
        registerWorkflowActions({
            [ACTION_NEW_MESSAGE]: COMPOSE_MAIL_CLICKED,
            [ACTION_LOAD_MORE_IN_TABLE]: LOAD_MORE_MAIL_STARTED,
            [ACTION_ON_LOAD_MORE_ITEMS_SUCCESS]: LOAD_MORE_MAIL_ENDED,
            [ACTION_ON_LOAD_MORE_CONVERSATIONS_SUCCESS]: LOAD_MORE_MAIL_ENDED,
            [ACTION_SELECT_FOCUSED_PIVOT]: FOCUSED_OTHER_TAB_CLICKED,
            [ACTION_SELECT_FILTER]: FILTER_SELECTED,
        });
    }
}
